import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, InputAdornment, Pagination, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { usePermissions } from '../../../../utils/usePermissions';
import { ConfirmDialog, EditEmptyState } from '../../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../../reports/summary/summaryStyles';
import { PRIMARY_BUTTON_SX } from '../../mathReports/mathTableStyles';
import EntryNetworkDialog from './EntryNetworkDialog';
import EntryOverridesTable from './EntryOverridesTable';
import EntryParamsTable from './EntryParamsTable';
import type { EntryNetworkPerms } from './EntryParamsTable';
import EntryPersonalDialog from './EntryPersonalDialog';
import EntryScheduleDialog from './EntryScheduleDialog';
import {
  KIND_LABELS, filterOverrides, formatMoment, overridePharmacyLabel, overrideValue, pageCount, pageSlice, ruleText
} from './revenueEntryCalc';
import type { EntryKind, EntryOverride } from './revenueEntryApi';
import { useRevenueEntry } from './useRevenueEntry';

const BORDER = '1px solid #e2e8f0';

const PERM = {
  schedule: 'tables.revenue.entry.schedule',
  extend: 'tables.revenue.entry.extend',
  unlock: 'tables.revenue.entry.unlock',
  personal: 'tables.revenue.entry.personal',
  date: 'tables.revenue.entry.date',
  dateNetwork: 'tables.revenue.entry.date_network'
};

type DialogTarget =
  | { type: 'schedule' }
  | { type: 'network'; kind: EntryKind }
  | { type: 'personal' }
  | { type: 'clear'; kind: EntryKind }
  | { type: 'remove'; item: EntryOverride };

/** Диалог остается смонтированным на время закрытия, чтобы текст не пропадал посреди анимации; key - новая форма на каждое открытие */
interface DialogState {
  key: number;
  target: DialogTarget;
  open: boolean;
}

const SECTION_TITLE_SX = { fontSize: '0.9rem', fontWeight: 700, color: '#334155' };

const RevenueEntryManager: React.FC = () => {
  const { hasPerm } = usePermissions();
  const networkPerms: EntryNetworkPerms = {
    schedule: hasPerm(PERM.schedule),
    extend: hasPerm(PERM.extend),
    unlock: hasPerm(PERM.unlock),
    date: hasPerm(PERM.dateNetwork)
  };
  const canPersonal = hasPerm(PERM.personal);
  const canDate = hasPerm(PERM.date);
  const canRemove = useMemo(
    () => ({ extend: canPersonal, unlock: canPersonal, date: canDate }),
    [canPersonal, canDate]
  );
  const personalKinds = useMemo(() => {
    const kinds: EntryKind[] = [];
    if (canPersonal) kinds.push('extend', 'unlock');
    if (canDate) kinds.push('date');
    return kinds;
  }, [canPersonal, canDate]);

  const {
    data, loading, error, retry, pending, ensureFresh,
    changeSchedule, setNetwork, clearNetwork, saveOverrides, removeOverride
  } = useRevenueEntry();

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  useEffect(() => {
    document.title = 'Окно ввода | Zerde';
  }, []);

  const overrides = data ? data.overrides : null;
  const filtered = useMemo(() => (overrides ? filterOverrides(overrides, query) : []), [overrides, query]);
  const pages = pageCount(filtered.length);
  // После отмены исключения или поиска страница может оказаться за последней
  const currentPage = Math.min(page, pages);
  const visible = useMemo(() => pageSlice(filtered, currentPage), [filtered, currentPage]);

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setPage(1);
  }, []);
  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => setPage(value), []);

  const show = useCallback(
    (target: DialogTarget) => setDialog((prev) => ({ key: (prev ? prev.key : 0) + 1, target, open: true })),
    []
  );
  // Формы считают срок от текущего времени сервера: перед открытием данные обновляются, если они старше минуты
  const showForm = useCallback(
    async (target: DialogTarget) => {
      await ensureFresh();
      show(target);
    },
    [ensureFresh, show]
  );
  const closeDialog = useCallback(() => setDialog((prev) => prev && { ...prev, open: false }), []);

  const handleEditSchedule = useCallback(() => showForm({ type: 'schedule' }), [showForm]);
  const handleSetRule = useCallback((kind: EntryKind) => showForm({ type: 'network', kind }), [showForm]);
  const handleClearRule = useCallback((kind: EntryKind) => show({ type: 'clear', kind }), [show]);
  const handleAddOverride = useCallback(() => showForm({ type: 'personal' }), [showForm]);
  const handleRemove = useCallback((item: EntryOverride) => show({ type: 'remove', item }), [show]);

  const handleConfirm = useCallback(async () => {
    if (!dialog) return;
    const { target } = dialog;
    if (target.type === 'clear' && (await clearNetwork(target.kind))) closeDialog();
    if (target.type === 'remove' && (await removeOverride(target.item))) closeDialog();
  }, [dialog, clearNetwork, removeOverride, closeDialog]);

  const pharmacies = data && data.pharmacies;
  const canAdd = personalKinds.length > 0 && !!pharmacies;
  const noPharmacies = !!pharmacies && pharmacies.length === 0;

  const addButton = canAdd && (
    <Tooltip title={noPharmacies ? 'В вашей зоне видимости нет аптек' : ''} arrow>
      <span>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddOverride}
          disabled={noPharmacies}
          sx={PRIMARY_BUTTON_SX}
        >
          Добавить исключение
        </Button>
      </span>
    </Tooltip>
  );

  const renderOverrides = () => {
    if (!data) return null;
    if (data.overrides.length === 0) {
      return (
        <EditEmptyState
          icon={<StorefrontOutlinedIcon />}
          title="Исключений нет"
          text={canAdd ? 'Исключение действует для выбранных аптек до указанного срока, не дольше 24 часов.' : undefined}
          action={addButton || undefined}
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EditEmptyState icon={<SearchOffIcon />} title="Ничего не найдено" text="Исключений для таких аптек нет. Проверьте запрос." />
      );
    }
    return (
      <>
        <Paper
          elevation={0}
          sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
          style={{ opacity: loading ? 0.55 : 1 }}
        >
          <EntryOverridesTable items={visible} canRemove={canRemove} onRemove={handleRemove} />
        </Paper>
        {pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={pages} page={currentPage} onChange={handlePageChange} shape="rounded" color="primary" />
          </Box>
        )}
      </>
    );
  };

  const renderConfirmText = () => {
    if (!dialog || !data) return null;
    const { target } = dialog;
    if (target.type === 'clear') {
      return (
        <>
          <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
            Отменить «{KIND_LABELS[target.kind]}» для всей сети ({ruleText(target.kind, data.network[target.kind])})?
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
            Ввод сразу вернется к окну сети. Исключения аптек сохранятся до своего срока.
          </Typography>
        </>
      );
    }
    if (target.type === 'remove') {
      return (
        <>
          <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
            Отменить исключение «{KIND_LABELS[target.item.kind]}» для <b>{overridePharmacyLabel(target.item)}</b> ({overrideValue(target.item)}{' '}
            до {formatMoment(target.item.until)})?
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>Для аптеки сразу начнут действовать параметры сети.</Typography>
        </>
      );
    }
    return null;
  };

  if (error && !data) {
    return (
      <EditEmptyState
        icon={<ErrorOutlineIcon />}
        title="Не удалось загрузить окно ввода"
        text={error}
        action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
      />
    );
  }
  if (!data) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const target = dialog && dialog.target;

  return (
    <Box sx={{ width: '100%', maxWidth: 1100 }}>
      <Typography sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}>Вся сеть</Typography>
      <Paper
        elevation={0}
        sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease', mb: 4 }}
        style={{ opacity: loading ? 0.55 : 1 }}
      >
        <EntryParamsTable
          data={data}
          perms={networkPerms}
          onEditSchedule={handleEditSchedule}
          onSetRule={handleSetRule}
          onClearRule={handleClearRule}
        />
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, maxWidth: 520 }}>
          <Typography sx={{ ...SECTION_TITLE_SX, whiteSpace: 'nowrap' }}>Исключения для аптек</Typography>
          {data.overrides.length > 0 && (
            <>
              <TextField
                size="small"
                fullWidth
                placeholder="Поиск по аптеке"
                value={query}
                onChange={handleQueryChange}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                    </InputAdornment>
                  )
                }}
              />
              <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                Всего: <b>{filtered.length}</b>
              </Typography>
            </>
          )}
        </Box>
        {data.overrides.length > 0 && addButton}
      </Box>

      {renderOverrides()}

      {dialog && target && target.type === 'schedule' && (
        <EntryScheduleDialog
          key={dialog.key}
          open={dialog.open}
          schedule={data.schedule}
          saving={pending === 'schedule'}
          onSave={changeSchedule}
          onClose={closeDialog}
        />
      )}

      {dialog && target && target.type === 'network' && (
        <EntryNetworkDialog
          key={dialog.key}
          open={dialog.open}
          kind={target.kind}
          data={data}
          saving={pending === 'network'}
          onSave={setNetwork}
          onClose={closeDialog}
        />
      )}

      {dialog && target && target.type === 'personal' && pharmacies && personalKinds.length > 0 && (
        <EntryPersonalDialog
          key={dialog.key}
          open={dialog.open}
          kinds={personalKinds}
          data={data}
          pharmacies={pharmacies}
          saving={pending === 'overrides'}
          onSave={saveOverrides}
          onClose={closeDialog}
        />
      )}

      <ConfirmDialog
        open={!!dialog && dialog.open && !!target && (target.type === 'clear' || target.type === 'remove')}
        title={target && target.type === 'remove' ? 'Отменить исключение' : 'Отменить параметр'}
        confirmText="Отменить"
        cancelText="Не отменять"
        confirmColor="error"
        loading={pending === 'clear' || pending === 'remove'}
        maxWidth="xs"
        onConfirm={handleConfirm}
        onClose={closeDialog}
      >
        {renderConfirmText()}
      </ConfirmDialog>
    </Box>
  );
};

export default RevenueEntryManager;
